import React from 'react';
import { motion } from 'framer-motion';
import { Target } from 'lucide-react';
import { wrappedTranslations } from '../../../translations/wrapped';

interface GiantKillerCardProps {
    bestWin: {
        opponent: string;
        enemy_elo: number;
        elo_gain: number;
    } | null;
    currentElo: number;
    vsHigherWins: number;
    lang: 'en' | 'fr' | 'de' | 'it';
}

const GiantKillerCard: React.FC<GiantKillerCardProps> = ({ bestWin, currentElo, vsHigherWins, lang }) => {
    const t = wrappedTranslations[lang];
    return (
        <div className="h-full w-full flex flex-col items-center justify-center text-center p-6">
            <div className="max-w-4xl w-full flex flex-col items-center">
                <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring" }}
                >
                    <Target className="w-20 h-20 md:w-32 md:h-32 mb-8 mx-auto text-[#1C7AD6]" />
                </motion.div>

                <motion.h2
                    className="text-2xl sm:text-3xl md:text-4xl font-bold mb-6 md:mb-8 text-gray-900 tracking-tight"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                >
                    {t.giantKiller}
                </motion.h2>

                {bestWin ? (
                    <>
                        <motion.p
                            className="text-base md:text-lg text-gray-600 mb-6 md:mb-8 font-light"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.5 }}
                        >
                            {t.yourBestWin}
                        </motion.p>

                        {bestWin && (
                            <motion.div
                                className="bg-white border border-gray-200 rounded-2xl p-6 md:p-8 shadow-lg w-full max-w-2xl"
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 0.6, type: "spring" }}
                            >
                                <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-6 md:mb-8 break-words">{bestWin.opponent}</div>
                                <div className="flex items-center justify-center gap-4 md:gap-12 mb-6 md:mb-8">
                                    <div className="text-center">
                                        <div className="text-5xl md:text-7xl font-bold text-red-600">{bestWin.enemy_elo}</div>
                                        <div className="text-xs md:text-sm text-gray-500 uppercase tracking-wider mt-1 md:mt-2">{t.theirElo}</div>
                                    </div>
                                    <div className="text-xl md:text-3xl text-gray-400 font-thin">vs</div>
                                    <div className="text-center">
                                        <div className="text-5xl md:text-7xl font-bold text-[#1C7AD6]">{currentElo}</div>
                                        <div className="text-xs md:text-sm text-gray-500 uppercase tracking-wider mt-1 md:mt-2">{t.yourElo}</div>
                                    </div>
                                </div>
                                <div className="bg-gray-100 rounded-2xl px-4 py-3 md:px-6 md:py-4 border border-gray-200 inline-block">
                                    <div className="text-5xl md:text-7xl font-bold text-emerald-600">+{bestWin.elo_gain}</div>
                                    <div className="text-xs md:text-sm text-gray-600 mt-1">{t.eloPointsGained}</div>
                                </div>
                            </motion.div>
                        )}

                        <motion.div
                            className="mt-6 md:mt-10"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.8 }}
                        >
                            <div className="text-gray-600 text-base md:text-lg">
                                {t.defeatedHigherElo}{' '}
                                <span className="font-bold text-[#1C7AD6] text-xl md:text-2xl mx-1">{vsHigherWins}</span> {t.times}
                            </div>
                        </motion.div>
                    </>
                ) : (
                    <motion.p
                        className="text-base md:text-lg text-gray-500 font-light"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.6 }}
                    >
                        {t.keepChallenging}
                    </motion.p>
                )}
            </div>
        </div>
    );
};

export default GiantKillerCard;
